import React, { memo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import classNames from 'classnames';

import { useOverlayState } from '../../../hooks/useOverlayState';
import { useResponsiveBreakpoints } from '../../../hooks/useResponsiveBreakpoints';

import UsersIcon from '../../../design-system/Icons/UsersIcon';
import EditLineIcon from '../../../design-system/Icons/EditLineIcon';
import PlayProcessIcon from '../../../design-system/Icons/PlayProcessIcon';
import TimestampDisplay from '../../../design-system/TimestampDisplay/TimestampDisplay';
import DeletePlaybookModal from '../PlaybookDetailPage/DeletePlaybookModal/DeletePlaybookModal';
import { DeleteBin4LineIcon } from '../../../design-system/Icons';
import { ButtonIcon, SvgIcon } from '../../../design-system';

const PlaybookCard = ({ playbook, keysToMutate }) => {
    const navigate = useNavigate();
    const location = useLocation();

    const { id, name, icon_color, created_at, is_shared } = playbook;
    const {
        isOpened: isDeleteModalOpened,
        open: openDeleteModal,
        close: closeDeleteModal,
    } = useOverlayState();

    const isEditable = !is_shared;

    const cardContainerClassName = classNames(
        'px-4 py-3 max-w-full rounded-2 bg-white',
        'grid grid-rows-[auto_auto] md:grid-rows-1 md:grid-cols-[minmax(100px,_1fr)_24px_148px_62px] gap-x-4 gap-y-2 items-center',
        is_shared && 'grid-cols-[minmax(100px,_1fr)_24px_56px]',
        !is_shared && 'grid-cols-[minmax(100px,_1fr)_56px]'
    );

    const { isMobile } = useResponsiveBreakpoints();

    return (
        <li className={cardContainerClassName}>
            <div className="flex items-center gap-2">
                <SvgIcon color={icon_color} icon={PlayProcessIcon} size="medium" />
                <p className="font-body-bold text-body-bold-s md:text-body-bold-xs text-neutral-500 truncate">
                    {name}
                </p>
            </div>

            {(is_shared || !isMobile) && (
                <div className="flex items-center justify-center">
                    {is_shared && <SvgIcon color="#000000" size="medium" icon={UsersIcon} />}
                </div>
            )}

            <div className="pt-2 md:p-0 border-t-1 border-neutral-200 md:border-none row-start-2 row-end-2 col-span-full md:row-start-auto md:row-end-auto md:col-span-1">
                <TimestampDisplay
                    label="Created At"
                    timestamp={created_at}
                    dateTimeFormat="MM/dd/yyyy"
                />
            </div>

            <div className="flex items-center justify-end md:gap-1.5">
                <ButtonIcon
                    type="link"
                    size="xs"
                    icon={EditLineIcon}
                    onClick={() =>
                        navigate(`/configure/playbook/${id}`, { state: { from: location } })
                    }
                />
                <ButtonIcon
                    type="link"
                    size="xs"
                    state={isEditable ? 'default' : 'disabled'}
                    icon={DeleteBin4LineIcon}
                    onClick={openDeleteModal}
                />
            </div>

            {isDeleteModalOpened && (
                <DeletePlaybookModal
                    id={id}
                    name={name}
                    requestKeysToMutate={keysToMutate}
                    onClose={closeDeleteModal}
                />
            )}
        </li>
    );
};

export default memo(PlaybookCard);
